<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            :no-close-on-backdrop="vocabulary_term_modified"
            :no-close-on-esc="vocabulary_term_modified"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @shown="focusVocabularyTerm"
            @ok="checkVocabTermBeforeSubmit"
        >
            <template #modal-title>
                <div v-if="editable_vocabulary_term.vocabulary_term_id">
                    Edit Vocabulary Term
                    <div v-if="editable_vocabulary_term.term" class="text-muted">
                        <small>{{editable_vocabulary_term.term}}</small>
                    </div>
                </div>
                <div v-else>Create Vocabulary Term</div>
            </template>
            <div v-if="allow_vocab_cards" class="alert alert-warning">
                <b>Vocabulary Cards Are Enabled:</b>
                Character limits must be followed.
            </div>
            <b-form-group
                label="Term"
                label-for="term"
            >
            <div
                class="text-right mb-2"
                :class="{ 'text-danger': getCharCount(editable_vocabulary_term.term) > 24 }"
            >
                {{ getCharCount(editable_vocabulary_term.term) }} characters of 24
            </div>
                <b-form-input
                    type="text"
                    ref="vocabulary_term"
                    v-model="editable_vocabulary_term.term"
                    :state="validating && !editable_vocabulary_term.term ? false : null"
                />

                <b-form-invalid-feedback v-if="term_exists == true">
                    <div class="d-flex align-items-center">
                        <div class="mr-1">Term already exists for this article.</div>
                    </div>
                </b-form-invalid-feedback>

                <b-form-invalid-feedback v-if="validating && !editable_vocabulary_term.term">
                    Term is required
                </b-form-invalid-feedback>
            </b-form-group>

            <div>
                <b-form-group
                    label="Definition"
                    label-for="definition"
                >
                <div
                    class="text-right mb-2"
                    :class="{ 'text-danger': getCharCount(editable_vocabulary_term.definition) > 195 }"
                >
                    {{ getCharCount(editable_vocabulary_term.definition) }} characters of 195
                </div>
                    <b-form-textarea
                        v-model="editable_vocabulary_term.definition"
                        :state="validating && !editable_vocabulary_term.definition ? false : null"
                    />

                    <b-form-invalid-feedback :state="validating && !editable_vocabulary_term.definition ? false : null">
                        Definition is required
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>

            <div>
                <b-form-group
                    label="Sentence"
                    label-for="sentence"
                >
                <div
                    class="text-right mb-2"
                    :class="{ 'text-danger': getCharCount(editable_vocabulary_term.sentence) > 101 }"
                >
                    {{ getCharCount(editable_vocabulary_term.sentence) }} characters of 101
                </div>
                <TipTap
                    v-model="editable_vocabulary_term.sentence"
                    :menu_items="['bold', 'italic', 'underline']"
                    :exclude_table="true"
                    :exclude_save="true"
                    :exclude_copy="true"
                />
                <div
                    :class="{ 'text-danger': getCharCount(editable_vocabulary_term.sentence) > 101 }"
                    >
                </div>
                </b-form-group>
            </div>

            <div v-if="editable_vocabulary_term.image" :class="{'merlin-asset-border': isMerlinMedia}">
                <div v-if="isMerlinMedia" class="merlin-asset-wrapper">
                    <span class="merlin-asset-warning">
                        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width />
                        Merlin Asset
                    </span>
                </div>
                <b-form-group
                    label="Image"
                >
                    <b-img
                        :src="editable_vocabulary_term.image.src"
                        width="150"
                        height="auto"
                        class="p-0 mx-2 mb-3"
                        thumbnail>
                    </b-img>
                    <div class="text-left"> Image Credit</div>
                    <div
                        class="text-right mb-2"
                        :class="{ 'text-danger': getCharCount(editable_vocabulary_term.image.credit) > 35 }"
                    >
                        <span  >{{ getCharCount(editable_vocabulary_term.image.credit) }} characters of 35</span>
                    </div>
                    <b-form-input
                        type="text"
                        ref="image_credit"
                        v-model="editable_vocabulary_term.image.credit"
                    />
                </b-form-group>
            </div>

            <b-btn v-if="editable_vocabulary_term.image" size="sm" class='d-block my-2' variant="danger" @click="removeImage">
                <font-awesome-icon :icon="['fad', 'times-square']" /> Remove Image
            </b-btn>

            <b-dropdown
                    size="sm"
                    class='mb-3'
                    variant="outline-primary"
                    dropup
                    right
                >
                <template #button-content>
                    <font-awesome-icon :icon="['fad', 'plus-square']" /><span v-if="editable_vocabulary_term.image"> Change Image</span><span v-else> Add Image</span>
                </template>
                <b-dropdown-item @click.prevent="() => {show_add_related_media_modal = true}">
                    <font-awesome-icon :icon="['fad', 'plus-square']" class="text-primary" fixed-width /> Import SBP Image
                </b-dropdown-item>
            </b-dropdown>

            <template #modal-footer>
                <b-button
                    variant="light"
                    @click="cancel"
                >
                    Cancel
                </b-button>
                <b-button
                    :variant="editable_vocabulary_term.vocabulary_term_id ? 'primary' : 'success'"
                    :disabled="checking_term"
                    @click="checkVocabTermBeforeSubmit"
                >
                    <div v-if="editable_vocabulary_term.vocabulary_term_id">
                        <font-awesome-icon :icon="['fad', 'check-square']" fixed-width /> Save Changes
                    </div>
                    <div v-else>
                        <font-awesome-icon :icon="['fad', 'plus-square']" fixed-width /> Create Vocabulary Term
                    </div>
                </b-button>
            </template>
        </b-modal>

        <add-related-media-modal
            v-model="show_add_related_media_modal"
            :type="'image'"
            @link="linkRelatedMedia"
            @hide="show_add_related_media_modal = false"
        />
    </div>
</template>

<style lang="scss" scoped>

</style>

<script>
import { mapState } from 'vuex'
import AddRelatedMediaModal from '@/components/modals/article/related_media/AddRelatedMediaModal'
import TipTap from '@/components/tiptap/TipTap'

export default {
    data: () => ({
        editable_vocabulary_term: {},
        editable_vocabulary_term_cache: {},
        show_add_related_media_modal: false,

        existing_vocabulary_term: {},

        checking_term: false,
        term_checked: false,

        term_exists: null
    }),
    props: [
        'value',
        'article',
        'vocabulary_term',
        'vocabulary_terms',
    ],
    components: {
        AddRelatedMediaModal,
        TipTap,
    },
    mixins: [

    ],
    computed: {
        ...mapState({
            articles: state => state.unit.articles,
            validating: state => state.utilities.validating,
            publication: state => state.publications.publication,
        }),
        modal_id () {
            let modal_id = 'vocabulary-term-modal'

            return modal_id
        },
        vocabulary_term_modified () {
            let vocabulary_term_modified = false

            if (
                !_.isEqual(this.editable_vocabulary_term.definition, this.editable_vocabulary_term_cache.definition) ||
                !_.isEqual(this.editable_vocabulary_term.clues, this.editable_vocabulary_term_cache.clues) ||
                !_.isEqual(this.editable_vocabulary_term.image, this.editable_vocabulary_term_cache.image) ||
                !_.isEqual(this.editable_vocabulary_term.term, this.editable_vocabulary_term_cache.term) ||
                !_.isEqual(this.editable_vocabulary_term.sentence, this.editable_vocabulary_term_cache.sentence)
            ) {
                vocabulary_term_modified = true
            }

            return vocabulary_term_modified
        },
        isMerlinMedia() {
            const termHasImage = this.editable_vocabulary_term &&
                this.editable_vocabulary_term.image &&
                this.editable_vocabulary_term.image.src
            if (!termHasImage) {
                return false
            }

            return this.editable_vocabulary_term.image.src.toLowerCase().includes('merlinone')
        },
        allow_vocab_cards() {
            if (
                !this.publication ||
                this.publication[0] === undefined ||
                this.publication[0].allow_vocab_cards === undefined
            ) {
                return false
            }

            return this.publication[0].allow_vocab_cards
        },
    },
    watch: {
        value: {
            handler (value) {
                if (value == true) {
                    this.$store.commit('setValidating', false)

                    if (this.vocabulary_term) {
                        this.editable_vocabulary_term = _.cloneDeep(this.vocabulary_term)
                        this.editable_vocabulary_term_cache = _.cloneDeep(this.vocabulary_term)
                    }
                }
            }
        }
    },
    methods: {
        checkVocabularyTerm: _.debounce(function (vocabulary_term) {

            this.clearVocabularyTermCheck()

            this.checking_term = true

            if (vocabulary_term) {
                this.axios.post('/api/articles/vocabulary_term', {
                    vocabulary_term: vocabulary_term.term
                })
                .then(response => {
                    let vocabulary_terms = []

                    if (response.data) {
                        vocabulary_terms = response.data
                        this.existing_vocabulary_term = vocabulary_terms[0]
                    }

                    if (vocabulary_terms.length) {
                        _.forEach(vocabulary_terms, vocabulary_term => {
                            if (_.find(this.vocabulary_terms, {vocabulary_term_id: vocabulary_term.id})) {
                                this.term_exists = true
                            }
                        })
                    } else {
                        this.term_exists = false
                    }

                    this.term_checked = true
                    this.checking_term = false
                })
                .catch(error => {
                    if (error) {
                        this.clearVocabularyTermCheck()
                    }
                })
            } else {
                this.clearVocabularyTermCheck()
            }
        }, 1000),
        clearVocabularyTermCheck () {
            this.$store.commit('setValidating', false)

            this.existing_vocabulary_term = {}

            this.term_checked = false
            this.checking_term = false
            this.term_exists = null
        },
        async checkVocabTermBeforeSubmit () {
            if (this.editable_vocabulary_term.vocabulary_term_id && this.editable_vocabulary_term.term !== this.editable_vocabulary_term_cache.term) {

                const result = await this.$swal({
                    html: 'Changes have been made to this vocabulary term. If you proceed, these changes will be applied to all instances where this term is used throughout all articles and publications.',
                    showCancelButton: true,
                    confirmButtonText: 'Cancel',
                    cancelButtonText: 'Update Term',
                    reverseButtons: true
                });

                if (result.value) {
                    this.hide()
                }
                else{
                    this.saveVocabularyTerm();        
                    this.hide()
                }
            } else {
                this.saveVocabularyTerm();
            }
        },
        saveVocabularyTerm () {
            this.$store.commit('setValidating', true)

            if (!this.validated()) { return }

            if (this.vocabulary_term_modified) {
                if (
                    this.existing_vocabulary_term &&
                    this.existing_vocabulary_term.id
                ) {
                    this.$emit('update', this.editable_vocabulary_term, this.existing_vocabulary_term, this.editable_vocabulary_term, this.editable_vocabulary_term.vocabulary_term_id)
                } else if (
                    this.editable_vocabulary_term &&
                    this.editable_vocabulary_term.vocabulary_term_id
                ) {
                    this.$emit('update', this.editable_vocabulary_term)
                } else {
                    this.$emit('create', this.editable_vocabulary_term)
                }
            } else {
                this.hide()
            }
        },
        validated () {
            let valid = true

            if (!this.editable_vocabulary_term.term) {
                valid = false
            }

            if (!this.editable_vocabulary_term.definition) {
                valid = false
            }

            if (
                this.editable_vocabulary_term.clues &&
                this.editable_vocabulary_term.clues.length
            ) {
                _.forEach(this.editable_vocabulary_term.clues, clue => {
                    if (!clue.clue) {
                        valid = false
                    }
                })
            }

            return valid
        },
        addClue () {
            if (!this.editable_vocabulary_term.clues) {
                this.$set(this.editable_vocabulary_term, 'clues', [])
            }

            this.editable_vocabulary_term.clues.push({})
        },
        removeClue (index) {
            if (this.editable_vocabulary_term.clues) {
                this.editable_vocabulary_term.clues.splice(index, 1)
            }
        },
        focusVocabularyTerm () {
            if (
                this.$refs.vocabulary_term &&
                (!this.editable_vocabulary_term || this.editable_vocabulary_term && !this.editable_vocabulary_term.vocabulary_term_id)
            ) {
                this.$refs.vocabulary_term.focus()
            }
        },
        cancel (event) {
            event.preventDefault()

            if (this.vocabulary_term_modified) {
                this.$swal({
                    html: 'Changes have been made to this vocabulary term. If you proceed, all changes will be lost.',
                    showCancelButton: true,
                    confirmButtonText: 'Discard Changes',
                    cancelButtonText: 'Keep Editing',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.hide()
                    }
                })
            } else {
                this.hide()
            }
        },
        hide () {
            this.$emit('hide')
        },
        hidden () {
            this.clearVocabularyTermCheck()

            this.editable_vocabulary_term = {}
            this.editable_vocabulary_term_cache = {}

            this.$emit('hide')
        },
        linkRelatedMedia(asset) {
            if (asset) {
                this.editable_vocabulary_term.image = {}
                this.editable_vocabulary_term.image.src = asset.url
                this.editable_vocabulary_term.image.alt = asset.alt
                this.editable_vocabulary_term.image.related_media_id = asset.id
                this.editable_vocabulary_term.image.desription = asset.description
                this.editable_vocabulary_term.image.caption = asset.caption
                this.editable_vocabulary_term.image.credit = asset.credit
            }
            this.$set(asset, 'linking', false)
            this.show_add_related_media_modal = false
        },
        removeImage() {
            this.$set(this.editable_vocabulary_term, 'image', null)
        },
        getCharCount(html = "") {
            return this.htmlToPlainText(html).length;
        },
        htmlToPlainText(html = "") {
            const div = document.createElement("div");
            div.innerHTML = html;

            return (div.textContent || div.innerText || "")
            .replace(/\u00A0/g, " "); // convert &nbsp;
        },
    }
}
</script>
